/** Self-contained info panel widget for the tree detail view — shows hover details for individual nodes or a default hint. */
import Phaser from 'phaser';
import type { V4NodeDefinition, V4ApotheosisDefinition } from '../utils/Types';
import type { AttunementComponent } from '../components/AttunementComponent';
import type { SkillProgressionComponent } from '../components/SkillProgressionComponent';
import { crispText, CRISP_SCALE } from './CrispUtil';
import {
  Font, Hex,
  NODE_INFO_HINT_ALPHA, NODE_INFO_TYPE_ALPHA,
  NODE_INFO_FLAVOR_ALPHA, NODE_INFO_DESC_ALPHA,
} from './Theme';
import { NODE_TYPE_LABELS } from '../utils/Types';

/** Configuration passed to TreeInfoPanel at construction time. */
export interface TreeInfoPanelConfig {
  /** Right-edge x coordinate for right-aligned text. */
  rightX: number;
  /** Top y coordinate for the first line of text. */
  topY: number;
}

/**
 * Manages all display objects for the skill-tree hover info panel.
 *
 * The panel owns its Phaser text objects entirely — callers interact only
 * through the three public display methods and `destroy()`. No reference to
 * any external object list is needed.
 */
export class TreeInfoPanel {
  private scene: Phaser.Scene;
  private rightX: number;
  private topY: number;

  /** The steady-state hint text ("hover a node for details"). */
  private hintText: Phaser.GameObjects.Text | null = null;

  /** All text objects produced by showNodeInfo — replaced on each hover. */
  private nodeInfoObjects: Phaser.GameObjects.Text[] = [];

  /** Optional references used to compute the unlock/equip status suffix. */
  private attunementRef: AttunementComponent | null = null;
  private skillProgressionRef: SkillProgressionComponent | null = null;

  /** @param scene - The Phaser scene that will own the display objects. */
  constructor(scene: Phaser.Scene, config: TreeInfoPanelConfig) {
    this.scene = scene;
    this.rightX = config.rightX;
    this.topY = config.topY;
  }

  /**
   * Attach the component references used to compute the node status suffix.
   * Call this once after the components are resolved (e.g. at render time).
   */
  setComponents(
    attunement: AttunementComponent,
    skillProgression: SkillProgressionComponent,
  ): void {
    this.attunementRef = attunement;
    this.skillProgressionRef = skillProgression;
  }

  /**
   * Show the default "hover a node for details" hint.
   * Pass new coordinates only when repositioning; subsequent calls without
   * coordinates re-use the last known position.
   */
  showDefault(rightX?: number, topY?: number): void {
    if (rightX !== undefined) this.rightX = rightX;
    if (topY !== undefined) this.topY = topY;

    this.clearAll();

    this.hintText = crispText(
      this.scene,
      this.rightX,
      this.topY,
      'Hover a node for details',
      {
        fontFamily: Font.family,
        fontSize: Font.sizeWorld,
        color: Hex.textMuted,
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(1, 0).setAlpha(NODE_INFO_HINT_ALPHA);
  }

  /** Show detailed info for the given node, replacing whatever is currently displayed. */
  showNodeInfo(node: V4NodeDefinition | V4ApotheosisDefinition): void {
    this.clearAll();

    const wrapWidth = 280 * CRISP_SCALE;
    let curY = this.topY;
    const lineGap = 3;

    // Node name — bold, bright, right-aligned
    const nameText = crispText(
      this.scene,
      this.rightX,
      curY,
      node.name.toUpperCase(),
      {
        fontFamily: Font.family,
        fontSize: Font.sizeSmall,
        color: Hex.textPrimary,
        fontStyle: 'bold',
        align: 'right',
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(1, 0);
    this.nodeInfoObjects.push(nameText);
    curY += nameText.displayHeight + lineGap;

    // Type + unlock/equip status suffix
    const typeLabel = NODE_TYPE_LABELS[node.nodeType] ?? node.nodeType;

    let statusSuffix = '';
    if (this.attunementRef && this.skillProgressionRef) {
      const isUnlocked = this.skillProgressionRef.isUnlocked(node.id);
      if (!isUnlocked) statusSuffix = '  ·  Locked';
      else if (this.attunementRef.isAttuned(node.id)) statusSuffix = '  ·  Equipped';
    }

    const typeText = crispText(
      this.scene,
      this.rightX,
      curY,
      `${typeLabel}${statusSuffix}`,
      {
        fontFamily: Font.family,
        fontSize: Font.sizeXs,
        color: Hex.textPrimary,
        align: 'right',
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(1, 0).setAlpha(NODE_INFO_TYPE_ALPHA);
    this.nodeInfoObjects.push(typeText);
    curY += typeText.displayHeight + lineGap * 2;

    // Flavor text (italic, quoted) — present on most but not all node types
    if ('flavorText' in node && node.flavorText) {
      const flavorText = crispText(
        this.scene,
        this.rightX,
        curY,
        `"${node.flavorText}"`,
        {
          fontFamily: Font.family,
          fontSize: Font.sizeXs,
          color: Hex.textPrimary,
          fontStyle: 'italic',
          align: 'right',
          wordWrap: { width: wrapWidth },
        } as Phaser.Types.GameObjects.Text.TextStyle,
      ).setOrigin(1, 0).setAlpha(NODE_INFO_FLAVOR_ALPHA);
      this.nodeInfoObjects.push(flavorText);
      curY += flavorText.displayHeight + lineGap * 2;
    }

    // Description — always present
    const descText = crispText(
      this.scene,
      this.rightX,
      curY,
      node.description,
      {
        fontFamily: Font.family,
        fontSize: Font.sizeXs,
        color: Hex.textPrimary,
        align: 'right',
        wordWrap: { width: wrapWidth },
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(1, 0).setAlpha(NODE_INFO_DESC_ALPHA);
    this.nodeInfoObjects.push(descText);
  }

  /** Destroy all display objects owned by this panel. */
  destroy(): void {
    this.clearAll();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Destroy every Phaser object currently managed by the panel. */
  private clearAll(): void {
    if (this.hintText) {
      this.hintText.destroy();
      this.hintText = null;
    }
    for (const obj of this.nodeInfoObjects) obj.destroy();
    this.nodeInfoObjects = [];
  }
}
